import React, { useState } from "react";
import { 
    Menu, 
    X, 
    Phone, 
    Mail, 
    MessageCircle, 
    User, 
    Send, 
    Download, 
    ChevronDown 
} from "lucide-react";
import { Link } from "react-router-dom";
import FocusLogo from "../assets/Focus Logo.jpg";

// The 4 Primary Links
const mainNavItems = [
    { label: "Home", path: "/" },
    { label: "Services", path: "/services" },
    { label: "Industries", path: "/industries" },
    { label: "About", path: "/about" },
];

// Dropdown 1: Resources
const resourceItems = [
    { label: "Technical Procedures", path: "/resources/procedures" },
    { label: "ASME Standards", path: "/resources/asme" },
    { label: "ISO Standards", path: "/resources/iso" },
    { label: "NDT Guidelines", path: "/resources/guidelines" },
];

// Dropdown 2: Company (The remaining items)
const companyItems = [
    { label: "Career", path: "/career" },
    { label: "Contact Us", path: "/contact" },
    { label: "Client Portal", path: "https://focusinspection-portal.web.app/" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [resDropdown, setResDropdown] = useState(false);
    const [compDropdown, setCompDropdown] = useState(false);

    return (
        <header className="sticky top-0 z-50 w-full bg-white shadow-md">
            {/* Top Info Bar - Same Design */}
            <div className="w-full bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center justify-between text-sm text-blue-900">
                    <span className="font-medium">GST: 27BFZPM9987H1Z3</span>
                    <div className="flex items-center gap-4">
                        <a href="/FocusBrochureNew.pdf" download className="flex items-center gap-1 px-3 py-1 rounded-full bg-red-600 text-white font-semibold shadow-md hover:bg-red-700 transition flex-shrink-0">
                            <Download size={14} />
                            <span className="hidden sm:inline">Download Brochure</span>
                            <span className="sm:hidden">Brochure</span>
                        </a>
                        <a href="tel:+919833205915" className="hidden lg:flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-200 transition">
                            <Phone size={16} /> +91 9833205915
                        </a>
                        <a href="https://wa.me/919833205915" target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-green-200 transition text-green-700 font-medium">
                            <MessageCircle size={16} /> WhatsApp
                        </a>
                    </div>
                </div>
            </div>

            {/* Main Navbar */}
            <div className="bg-white/90 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    {/* Brand - Same Design */}
                    <Link to="/" className="flex items-center gap-3 flex-nowrap cursor-pointer">
                        <div className="h-15 w-20 rounded-xl shadow-md overflow-hidden flex-shrink-0">
                            <img src={FocusLogo} alt="Logo" className="h-full w-full object-cover" loading="lazy" />
                        </div>
                        <div className="leading-tight flex-shrink">
                            <p className="text-base sm:text-lg font-extrabold text-blue-800 whitespace-nowrap">Focus Inspection Technology</p>
                            <p className="text-xs text-gray-500 -mt-1 whitespace-nowrap">Non-Destructive Testing (NDT)</p>
                        </div>
                    </Link>

                    {/* Desktop Nav - 4 Main + 2 Dropdowns */}
                    <nav className="hidden md:flex items-center gap-6">
                        {mainNavItems.map((item) => (
                            <Link key={item.label} to={item.path} className="text-sm font-medium text-gray-700 hover:text-blue-700 transition">
                                {item.label}
                            </Link>
                        ))}

                        {/* Resources Dropdown */}
                        <div className="relative" onMouseEnter={() => setResDropdown(true)} onMouseLeave={() => setResDropdown(false)}>
                            <button className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-blue-700 transition py-5">
                                Resources <ChevronDown size={14} className={`transition-transform ${resDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {resDropdown && (
                                <div className="absolute top-full left-0 w-52 bg-white shadow-xl border border-gray-100 rounded-b-xl py-2 z-50">
                                    {resourceItems.map((sub) => (
                                        <Link key={sub.label} to={sub.path} className="block px-4 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-700 transition">{sub.label}</Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Company Dropdown */}
                        <div className="relative" onMouseEnter={() => setCompDropdown(true)} onMouseLeave={() => setCompDropdown(false)}>
                            <button className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-blue-700 transition py-5">
                                Company <ChevronDown size={14} className={`transition-transform ${compDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {compDropdown && (
                                <div className="absolute top-full right-0 w-48 bg-white shadow-xl border border-gray-100 rounded-b-xl py-2 z-50">
                                    {companyItems.map((sub) => (
                                        <Link key={sub.label} to={sub.path} className="block px-4 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-700 transition">{sub.label}</Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* CTAs */}
                        <Link to="/getquote" className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 shadow-md hover:bg-blue-700 transition">
                            <Send size={16} /> Get a Quote
                        </Link>
                        <Link to="https://focusinspection-portal.web.app/" className="px-4 py-2 rounded-xl bg-teal-600 text-white text-sm font-semibold flex items-center gap-2 shadow-md hover:bg-teal-500 transition">
                            <User size={16} /> Login
                        </Link>
                    </nav>

                    {/* Mobile Toggle */}
                    <button className="md:hidden p-2 rounded-lg border border-gray-200" onClick={() => setOpen(!open)}>
                        {open ? <X /> : <Menu />}
                    </button>
                </div>

                {/* Mobile Menu - Same Logic */}
                {open && (
                    <div className="md:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-2">
                        {mainNavItems.map((item) => (
                            <Link key={item.label} to={item.path} className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50" onClick={() => setOpen(false)}>{item.label}</Link>
                        ))}
                        
                        <div className="py-2 border-t border-gray-50">
                            <p className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Resources</p>
                            {resourceItems.map((sub) => (
                                <Link key={sub.label} to={sub.path} className="block px-6 py-2 text-sm text-gray-600" onClick={() => setOpen(false)}>{sub.label}</Link>
                            ))}
                        </div>

                        <div className="py-2 border-t border-gray-50">
                            <p className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">More</p>
                            {companyItems.map((sub) => (
                                <Link key={sub.label} to={sub.path} className="block px-6 py-2 text-sm text-gray-600" onClick={() => setOpen(false)}>{sub.label}</Link>
                            ))}
                        </div>

                        <div className="flex flex-col gap-3 pt-4">
                            <Link to="/getquote" className="w-full py-2 rounded-full bg-blue-600 text-white text-center font-semibold flex items-center justify-center gap-2"><Send size={16}/> Get a Quote</Link>
                            <Link to="/login" className="w-full py-2 rounded-xl bg-teal-600 text-white text-center font-semibold flex items-center justify-center gap-2"><User size={16}/> Login</Link>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
}